import { SmsRcsMessagingTermsContent } from "./SmsRcsMessagingTerms";
import type { DocMeta } from "./types";

const SLUG = "terms-of-service";
const id = (s: string) => `${SLUG}-${s}`;

export const TERMS_OF_SERVICE_META: DocMeta = {
  slug: SLUG,
  title: "Terms of Service",
  subtitle: "Terms for using Logical Health websites and public services",
  description:
    "The terms that govern use of the Logical Health website and public-facing services.",
  lastUpdated: "July 3, 2026",
  sections: [
    { id: id("introduction"), title: "Introduction" },
    { id: id("messaging-terms"), title: "1. SMS/RCS Messaging Terms" },
    {
      id: id("eligibility"),
      title: "2. Eligibility and Account Responsibility",
    },
    { id: id("acceptable-use"), title: "3. Acceptable Use" },
    {
      id: id("submissions-and-communications"),
      title: "4. Submissions and Communications",
    },
    {
      id: id("healthcare-disclaimers"),
      title: "5. Healthcare, Benefits, and AI Disclaimers",
    },
    { id: id("intellectual-property"), title: "6. Intellectual Property" },
    {
      id: id("third-party-services"),
      title: "7. Third-Party Services and Links",
    },
    { id: id("disclaimers"), title: "8. Disclaimers" },
    {
      id: id("limitation-of-liability"),
      title: "9. Limitation of Liability",
    },
    { id: id("indemnification"), title: "10. Indemnification" },
    {
      id: id("changes-to-these-terms"),
      title: "11. Changes to These Terms",
    },
    {
      id: id("governing-law-and-disputes"),
      title: "12. Governing Law and Disputes",
    },
    { id: id("general-terms"), title: "13. General Terms" },
    { id: id("contact"), title: "14. Contact" },
  ],
};

export function TermsOfService() {
  return (
    <>
      <section id={id("introduction")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction</h2>
        <p>
          These Terms of Service (&ldquo;Terms&rdquo;) govern your access to
          and use of the Logical Health website at{" "}
          <strong>https://logical.health</strong>, public web pages, contact
          forms, demo requests, SMS/RCS opt-in pages, and other public-facing
          services made available by Logical Health, Inc. (&ldquo;Logical
          Health&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
          &ldquo;our&rdquo;).
        </p>
        <p>
          Logical Health provides healthcare technology and member-support
          services for benefits navigation, coverage questions, provider
          search, cost-estimate support, appointment navigation, and related
          customer deployments. Customer deployments, paid services,
          Business Associate Agreements, data protection agreements, service
          orders, and similar signed contracts are governed by those written
          agreements. If these Terms conflict with a signed agreement
          between Logical Health and a customer, the signed agreement
          controls for that customer relationship.
        </p>
        <p>
          Logical Health does not sell, rent, buy, or share SMS/RCS opt-in
          consent data with third parties or affiliates for their own
          marketing or promotional purposes.
        </p>
        <p>
          No mobile information will be shared with third party/affiliates
          for marketing or promotional purposes.
        </p>
        <p className="font-semibold text-lg mt-6">
          Please read these Terms, our{" "}
          <a
            href="/legal#privacy-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Privacy Policy
          </a>
          , and our{" "}
          <a
            href="/cookie-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Cookie Policy
          </a>{" "}
          carefully. By using the public website or public-facing services,
          you agree to these Terms.
        </p>
      </section>

      <section id={id("messaging-terms")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          1. SMS/RCS Messaging Terms
        </h2>
        <SmsRcsMessagingTermsContent
          makeId={(suffix) => id(`messaging-terms-${suffix}`)}
        />
        <p className="mt-8">
          Other messaging channels that Logical Health may offer, such as
          WhatsApp, are covered by our{" "}
          <a
            href="/messaging-terms"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Messaging Terms
          </a>{" "}
          page and the channel-specific disclosures presented at enrollment.
        </p>
      </section>

      <section id={id("eligibility")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          2. Eligibility and Account Responsibility
        </h2>
        <p>
          The public website and public-facing services are intended for
          users who are at least 18 years old. By using them, you represent
          that you are at least 18 and that your use complies with applicable
          law.
        </p>
        <p className="mt-4">
          If you access any password-protected, customer, administrator, or
          member-specific service, you are responsible for keeping your
          credentials confidential and for promptly notifying Logical Health
          or the applicable customer if you believe your access has been
          compromised.
        </p>
      </section>

      <section id={id("acceptable-use")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          3. Acceptable Use
        </h2>
        <p>When using the website or services, you agree not to:</p>
        <ul className="ml-6 space-y-2">
          <li>
            Use the website or services for unlawful, harmful, or abusive
            purposes.
          </li>
          <li>
            Attempt to gain unauthorized access to systems, accounts,
            networks, data, source code, or non-public areas.
          </li>
          <li>
            Interfere with, disrupt, overload, scan, probe, or test the
            security or availability of the website or services without
            written authorization.
          </li>
          <li>
            Upload, transmit, or introduce malware, malicious code, or
            harmful content.
          </li>
          <li>
            Scrape, harvest, or collect information from the website or
            services in a way that violates law, privacy rights, or these
            Terms.
          </li>
          <li>
            Impersonate another person or entity or misrepresent your
            affiliation with any person or entity.
          </li>
          <li>
            Submit content that is false, misleading, defamatory, obscene,
            discriminatory, harassing, or otherwise objectionable.
          </li>
          <li>
            Reverse engineer, decompile, disassemble, or attempt to derive
            source code, models, algorithms, or non-public logic except to
            the extent such restriction is prohibited by law.
          </li>
        </ul>
        <p className="mt-4">
          We may suspend, restrict, or terminate access, remove content, or
          take other appropriate action if we believe these Terms have been
          violated.
        </p>
      </section>

      <section id={id("submissions-and-communications")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          4. Submissions and Communications
        </h2>
        <p>
          If you submit a form, request a demo, send a message, schedule a
          meeting, or otherwise communicate with us, you represent that the
          information you provide is accurate and that you have the right to
          provide it. You authorize Logical Health to use that information
          to respond to you, provide requested information, operate the
          service, maintain records, and fulfill the purposes described in
          our Privacy Policy.
        </p>
        <p className="mt-4">
          You agree not to submit PHI, confidential customer information,
          credentials, secrets, or regulated data through public website
          forms unless the channel has been specifically approved for that
          purpose.
        </p>
      </section>

      <section id={id("healthcare-disclaimers")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          5. Healthcare, Benefits, and AI Disclaimers
        </h2>
        <p>
          <strong>
            Logical Health is not a medical provider, emergency service,
            insurer, or health plan.
          </strong>{" "}
          The website and services may support benefits navigation,
          coverage questions, claims explanations, provider search, and
          related administrative support, but they do not provide medical
          diagnosis, treatment, emergency care, or professional medical
          advice.
        </p>
        <p className="mt-4">
          Benefits, coverage, eligibility, provider network, and cost
          information may depend on health plan documents, customer data,
          provider data, and third-party systems. Always consult your
          health plan, plan documents, provider, or qualified healthcare
          professional for medical, coverage, emergency, or benefits
          decisions.
        </p>
      </section>

      <section id={id("intellectual-property")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          6. Intellectual Property
        </h2>
        <p>
          The website, services, software, text, graphics, logos, designs,
          trademarks, service marks, documentation, and other Logical Health
          materials are owned by Logical Health or its licensors and are
          protected by intellectual property and other laws. Except as
          expressly allowed by these Terms or a signed agreement, you may
          not copy, modify, distribute, publicly display, create derivative
          works from, or exploit Logical Health materials.
        </p>
        <p className="mt-4">
          Subject to these Terms, Logical Health grants you a limited,
          revocable, non-exclusive, non-transferable right to access and use
          the public website for its intended purpose.
        </p>
      </section>

      <section id={id("third-party-services")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          7. Third-Party Services and Links
        </h2>
        <p>
          The website may link to or use third-party services, including
          analytics, scheduling, communication, hosting, and form-delivery
          providers. Third-party services are governed by their own terms
          and privacy policies. Logical Health is not responsible for
          third-party websites or services that we do not control.
        </p>
      </section>

      <section id={id("disclaimers")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          8. Disclaimers
        </h2>
        <p>
          The website and public-facing services are provided &ldquo;as
          is&rdquo; and &ldquo;as available.&rdquo; To the fullest extent
          permitted by law, Logical Health disclaims all warranties,
          whether express, implied, statutory, or otherwise, including
          warranties of merchantability, fitness for a particular purpose,
          title, and non-infringement.
        </p>
        <p className="mt-4">
          We do not guarantee that the website or services will be
          uninterrupted, error-free, secure, accurate, complete, or
          available at all times.
        </p>
      </section>

      <section id={id("limitation-of-liability")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          9. Limitation of Liability
        </h2>
        <p>
          To the fullest extent permitted by law, Logical Health will not be
          liable for indirect, incidental, special, consequential, exemplary,
          or punitive damages, or for lost profits, lost revenue, lost data,
          business interruption, or similar losses arising out of or related
          to your use of, or inability to use, the website or public-facing
          services.
        </p>
        <p className="mt-4">
          For customer deployments or paid services governed by a signed
          agreement, liability limitations in that agreement control for
          that customer relationship.
        </p>
      </section>

      <section id={id("indemnification")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          10. Indemnification
        </h2>
        <p>
          To the extent permitted by law, you agree to defend, indemnify,
          and hold Logical Health harmless from claims, liabilities,
          damages, losses, and expenses arising from your misuse of the
          website or services, your violation of these Terms, your
          submissions, or your infringement or violation of another
          person&rsquo;s rights.
        </p>
      </section>

      <section id={id("changes-to-these-terms")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          11. Changes to These Terms
        </h2>
        <p>
          We may update these Terms from time to time to reflect changes in
          our services, legal requirements, or business practices. When we
          make changes, we will post the updated Terms on this page and
          update the &ldquo;Last Updated&rdquo; date. Your continued use of
          the website or public-facing services after the updated Terms are
          posted means you accept the updated Terms.
        </p>
      </section>

      <section id={id("governing-law-and-disputes")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          12. Governing Law and Disputes
        </h2>
        <p>
          These Terms are governed by the laws of the State of New York,
          without regard to conflict-of-law principles. Any dispute arising
          from or relating to these Terms or the public website will be
          resolved in the state or federal courts located in New York
          County, New York, unless a signed agreement between Logical Health
          and a customer provides otherwise.
        </p>
      </section>

      <section id={id("general-terms")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          13. General Terms
        </h2>
        <p>
          If any provision of these Terms is found unenforceable, the
          remaining provisions will remain in effect. Our failure to enforce
          a provision is not a waiver of our right to do so later. You may
          not assign these Terms without our prior written consent. Logical
          Health may assign these Terms as part of a merger, acquisition,
          corporate transaction, or transfer of assets.
        </p>
      </section>

      <section id={id("contact")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">14. Contact</h2>
        <p>
          For questions, feedback, or general inquiries about these Terms,
          contact us at{" "}
          <a
            href="mailto:hello@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            hello@logical.health
          </a>
          .
        </p>
        <p className="mt-4">
          For privacy, HIPAA, security, compliance, or DSAR inquiries,
          contact{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
          , and see our{" "}
          <a
            href="/legal#privacy-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="/hipaa-compliance"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            HIPAA &amp; Security Statement
          </a>
          .
        </p>
      </section>
    </>
  );
}
